<template>
  <div>
    <template v-if="otpExpired">
      <p class="text-body px-4 md:px-8">
        {{ $t('otp.codeExpired') }}
      </p>

      <BaseButton
        :variant="customisedBranding ? 'outline' : 'primary'"
        class="!font-medium !text-body w-full mt-6 mb-4"
        :loading="sendingCode"
        @click.native="handleSendCode"
      >
        {{ $t('otp.resendCode') }}
      </BaseButton>
    </template>

    <template v-else>
      <p class="text-body px-4 md:px-8">
        {{ $t('otp.codeRequiredMessage', { phone: mobileLastFour }) }}
      </p>

      <input
        :title="$t('otp.codeEnter')"
        type="text"
        inputmode="numeric"
        autocomplete="one-time-code"
        :placeholder="$t('otp.codeEnter')"
        :aria-label="$t('otp.code')"
        class="w-full text-body border border-solid border-formBorder rounded-small mb-0 pl-5 pr-16 py-4 placeholder-black placeholder-opacity-100"
        :class="{ 'border-danger outline-danger': !!verifyError }"
        v-model="otpCode"
        @input="handleInput"
        @keypress.enter="handleVerifyOtp"
        maxlength="6"
        required
      />

      <ValidationError
        :hasError="!!verifyError"
        :errorMessage="$t(verifyError)"
        :errorAdvice="$t('otp.codeIncorrectAdvice')"
      />

      <p class="text-left text-sm text-gray-600 mt-2 mb-0">
        {{ $t('otp.codeAdvice') }}
        {{ expiresIn ? $t('otp.expiresMessage', { expiresIn }) : '' }}
      </p>

      <BaseButton
        :variant="customisedBranding ? 'outline' : 'primary'"
        class="!font-medium !text-body w-full mt-6 mb-4"
        :disabled="!isValidOtp"
        :loading="verifyingOtp"
        @click.native="handleVerifyOtp"
      >
        {{$t('otp.verify')}}
      </BaseButton>

      <p
        v-if="newCodeSent"
        class="!text-body m-0"
      >
        {{ $t('otp.newCodeSent') }}
      </p>

      <p
        v-if="codesSent < 3"
        class="!text-body mt-0"
      >
        {{ $t('otp.notReceivedCode') }}
        <BaseButton
          variant="invisible"
          class="!inline-block !font-medium !text-body !text-blue !p-0 !mt-0 !no-underline"
          :loading="sendingCode"
          @click.native="handleSendCode"
        >
          {{ $t('otp.resendCode') }}
        </BaseButton>
      </p>

      <p
        v-else
        class="!text-body mt-0"
      >
        {{ $t('otp.codeSentMaxTimes') }}
      </p>
    </template>

      <ValidationError
        :hasError="!!requestError"
        :errorMessage="$t(requestError)"
      />
  </div>
</template>

<script setup>
import { defineProps, ref, onMounted, computed, watch, onUnmounted } from 'vue'

import BaseButton from '@/components/library/buttons/BaseButton.vue'
import ValidationError from '@/components/library/ValidationError.vue'

const props = defineProps({
  mobileLastFour: String,
  expires: Date,
  verifyError: [String, Boolean],
  requestError: [String, Boolean],
  codesSent: Number,
  verifyOtp: Function,
  sendCode: Function,
  customisedBranding: Boolean
})

const enableResendCode = ref(false)
const newCodeSent = ref(false)

const otpCode = ref('')
const verifyingOtp = ref(false)
const verifyError = ref(props.verifyError)
const sendingCode = ref(false)

watch(() => props.verifyError, (newError) => {
  verifyError.value = newError
})

const expiryTimer = ref(null)
const expiresIn = ref('')
const otpExpired = ref(false)

function updateExpiry () {
  if (!props.expires) {
    expiresIn.value = ''
    otpExpired.value = false
    return
  }

  const now = new Date()
  const diff = props.expires.getTime() - now.getTime()

  if (diff <= 0) {
    expiresIn.value = '0:00'
    otpExpired.value = true
    return
  }

  const minutes = Math.floor(diff / 60000)
  const seconds = Math.floor((diff / 1000) % 60)
  expiresIn.value = `${minutes}:${seconds.toString().padStart(2, '0')}`

  otpExpired.value = false

  // Clear the timer
  clearTimeout(expiryTimer.value)
  // Update in a second
  expiryTimer.value = setTimeout(updateExpiry, 1000)
}

watch(() => props.expires, () => {
  updateExpiry()
})

const isValidOtp = computed(() => otpCode.value.length === 6 && !verifyError.value)

function handleInput (event) {
  // Only allow numbers
  otpCode.value = event.target.value.replace(/[^0-9]/g, '')
  // Clear error
  verifyError.value = null
}

async function handleVerifyOtp () {
  if (!isValidOtp.value) {
    return
  }

  verifyingOtp.value = true
  await props.verifyOtp(otpCode.value)
  verifyingOtp.value = false
}

async function handleSendCode () {
  sendingCode.value = true

  if (await props.sendCode()) {
    newCodeSent.value = true
  }

  sendingCode.value = false
}

onMounted(() => {
  // Initial update
  updateExpiry()

  // Enable resend after 60 seconds
  setTimeout(() => {
    enableResendCode.value = true
  }, 60 * 1000)
})

onUnmounted(() => {
  if (expiryTimer.value) {
    clearTimeout(expiryTimer.value)
  }
})
</script>
